import * as THREE from 'three';
import { hexToWorld, type NaveLayout } from '@somnhold/sim';
import { Kit, place } from '../seasons/kit.ts';

/**
 * The walls of the Hermitage's nave (chapters 4 and 5 of the cleric's path, engine/ermita.ts) as one carved hall, not a
 * cell's block of stone after another: the floor's outline row by row, smoothed (the hex rows step half a cell in and
 * out), and along it a continuous wall: a stone bench at its foot, two string courses, a cornice, a pilaster at every
 * turn of the wall and between, and in its bays lancet windows (their glass faintly lit from the snow outside) and
 * niches with their candle stubs; the cells' doors as dark arches; behind the altar a carved retable of three arches
 * with the Hermitage's sun in the middle one. The rock cells behind are only a dark mass under the cornice's line.
 *
 * All of it in the layer's one merged mesh; the glows go to its glow layer (no scene lights).
 */

const STONE = ['#8f949c', '#868b93', '#989da5'];
const STONE_D = ['#6d727a', '#656a72', '#757a82'];
const PALE = '#a3a8b0';
const DARK = '#121418';
const GLASS = '#41506c';
const MASS = '#2a2d33';
const K = 1.2 / 1000;

export interface NaveWalls {
  /** The nearest point on the walls' face to a point (sim units) and the face's inward normal (world). */
  face(x: number, y: number): { p: THREE.Vector3; n: THREE.Vector3 } | null;
}

type Seg = { a: THREE.Vector2; b: THREE.Vector2; n: THREE.Vector2; h0: number; h1: number; end: boolean };

/** The side walls' height along the nave (world z): tall to the north, falling to the south (the camera's side). */
const heightAt = (z: number): number => {
  const r = z / (0.866 * 1.2);
  if (r <= -7) return 3.4;
  if (r <= -4) return 3.4 - ((r + 7) / 3) * 0.8;
  return Math.max(1.3, 2.6 - (r + 4) * 0.12);
};

export const naveWalls = (
  kit: Kit,
  L: NaveLayout,
  floorY: number,
  light: (p: THREE.Vector3, c: THREE.Color, size: number, a: number, flick?: number) => void,
  opts: { past: boolean; flame: THREE.Color; cold: THREE.Color },
): NaveWalls => {
  const R = L.radius;
  const rock = new Set(L.rocks.map((h) => `${h.q},${h.r}`));
  const solid = new Set([...L.mirrors, ...L.splits, L.altar, L.door].map((h) => `${h.q},${h.r}`));
  // The floor's extent on each row (world x of its outermost cells' outer edges).
  const ext = new Map<number, [number, number]>();
  for (let r = -R; r <= R; r++)
    for (let q = -R; q <= R; q++) {
      if (Math.max(Math.abs(q), Math.abs(r), Math.abs(q + r)) > R) continue;
      const k = `${q},${r}`;
      if (rock.has(k) && !solid.has(k)) continue;
      const x = hexToWorld(q, r).x * K;
      const e = ext.get(r);
      ext.set(r, e ? [Math.min(e[0], x), Math.max(e[1], x)] : [x, x]);
    }
  const rows = [...ext.keys()].filter((r) => Math.abs(r) < R).sort((a, b) => a - b);
  const r0 = rows[0] as number;
  const r1 = rows[rows.length - 1] as number;
  const zOf = (r: number): number => r * 0.866 * 1.2;
  // Smoothed: each row's wall as far out as its own and its neighbours' floor (no half-cell zigzag).
  const side = (s: -1 | 1): THREE.Vector2[] => {
    const pts: THREE.Vector2[] = [];
    for (const r of rows) {
      let x = s < 0 ? Infinity : -Infinity;
      for (const rr of [r - 1, r, r + 1]) {
        const e = ext.get(rr);
        if (!e || Math.abs(rr) >= R) continue;
        x = s < 0 ? Math.min(x, e[0]) : Math.max(x, e[1]);
      }
      pts.push(new THREE.Vector2(x + s * 0.62, zOf(r)));
    }
    // To the end walls.
    const first = pts[0] as THREE.Vector2;
    const last = pts[pts.length - 1] as THREE.Vector2;
    pts.unshift(new THREE.Vector2(first.x, zOf(r0) - 0.56));
    pts.push(new THREE.Vector2(last.x, zOf(r1) + 0.56));
    // Collinear runs merged into one long bay.
    const out: THREE.Vector2[] = [pts[0] as THREE.Vector2];
    for (let i = 1; i < pts.length - 1; i++) {
      const a = out[out.length - 1] as THREE.Vector2;
      const b = pts[i] as THREE.Vector2;
      const c = pts[i + 1] as THREE.Vector2;
      const cross = (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
      if (Math.abs(cross) > 1e-3) out.push(b);
    }
    out.push(pts[pts.length - 1] as THREE.Vector2);
    return out;
  };
  const left = side(-1);
  const right = side(1);
  const segs: Seg[] = [];
  const add = (a: THREE.Vector2, b: THREE.Vector2, inward: THREE.Vector2, end: boolean, h0?: number, h1?: number): void => {
    const d = b.clone().sub(a);
    let n = new THREE.Vector2(-d.y, d.x).normalize();
    if (n.dot(inward) < 0) n = n.negate();
    segs.push({ a, b, n, h0: h0 ?? heightAt(a.y), h1: h1 ?? heightAt(b.y), end });
  };
  for (let i = 0; i + 1 < left.length; i++) add(left[i] as THREE.Vector2, left[i + 1] as THREE.Vector2, new THREE.Vector2(1, 0), false);
  for (let i = 0; i + 1 < right.length; i++) add(right[i] as THREE.Vector2, right[i + 1] as THREE.Vector2, new THREE.Vector2(-1, 0), false);
  const nl = left[0] as THREE.Vector2;
  const nr = right[0] as THREE.Vector2;
  add(nl, nr, new THREE.Vector2(0, 1), true, 3.4, 3.4);
  // The south wall low (the cut-away), a gap for the nave's door (its own arch is drawn by the layer).
  const sl = left[left.length - 1] as THREE.Vector2;
  const sr = right[right.length - 1] as THREE.Vector2;
  const dx = hexToWorld(L.door.q, L.door.r).x * K;
  add(sl, new THREE.Vector2(dx - 0.95, sl.y), new THREE.Vector2(0, -1), true, 1.0, 1.0);
  add(new THREE.Vector2(dx + 0.95, sr.y), sr, new THREE.Vector2(0, -1), true, 1.0, 1.0);

  const box = new THREE.BoxGeometry(1, 1, 1);
  const prism = (() => {
    const sh = new THREE.Shape();
    sh.moveTo(-0.5, 0);
    sh.lineTo(0.5, 0);
    sh.lineTo(0, 1);
    sh.closePath();
    const g = new THREE.ExtrudeGeometry(sh, { depth: 1, bevelEnabled: false });
    g.translate(0, 0, -0.5);
    return g;
  })();
  const disc = new THREE.CylinderGeometry(0.5, 0.5, 1, 16);
  const y0 = floorY;
  // A piece on a wall: `u` along it (world), `y` up, `o` out from its face (into the nave), sized w × h × d.
  const on = (s: Seg, u: number, y: number, o: number, w: number, h: number, d: number, col: string, geo: THREE.BufferGeometry = box): void => {
    const dir = s.b.clone().sub(s.a).normalize();
    const x = s.a.x + dir.x * u + s.n.x * o;
    const z = s.a.y + dir.y * u + s.n.y * o;
    const yaw = Math.atan2(s.n.x, s.n.y);
    kit.add(geo, col, place(x, y0 + y, z, yaw, new THREE.Vector3(w, h, d)));
  };
  const at = (s: Seg, u: number, y: number, o: number): THREE.Vector3 => {
    const dir = s.b.clone().sub(s.a).normalize();
    return new THREE.Vector3(s.a.x + dir.x * u + s.n.x * o, y0 + y, s.a.y + dir.y * u + s.n.y * o);
  };
  const lancet = (s: Seg, u: number, h: number): void => {
    const wy = Math.min(h - 1.15, 1.35);
    const wh = Math.min(1.0, h - wy - 0.55);
    if (wh < 0.5) return;
    // The splay (dark), the glass (a cold blue, the snow's glare outside), the frame and its pointed head.
    on(s, u, wy + wh / 2, 0.01, 0.44, wh, 0.04, DARK);
    on(s, u, wy + wh / 2, 0.02, 0.3, wh - 0.06, 0.03, opts.past ? '#6a6458' : GLASS);
    on(s, u, wy + wh, 0.02, 0.44, 0.26, 0.04, DARK, prism);
    on(s, u, wy + wh, 0.03, 0.3, 0.18, 0.03, opts.past ? '#6a6458' : GLASS, prism);
    on(s, u, wy + wh / 2, 0.035, 0.03, wh - 0.06, 0.02, '#2a2d32');
    for (const k of [-1, 1]) on(s, u + k * 0.25, wy + wh / 2, 0.05, 0.07, wh + 0.04, 0.08, PALE);
    on(s, u, wy + wh + 0.02, 0.05, 0.56, 0.32, 0.08, PALE, prism);
    on(s, u, wy - 0.04, 0.08, 0.6, 0.07, 0.16, PALE);
    light(at(s, u, wy + wh * 0.6, 0.1), opts.past ? opts.flame : opts.cold, 1.25, opts.past ? 0.4 : 0.55, 0);
  };
  const niche = (s: Seg, u: number, h: number): void => {
    if (h < 1.6) return;
    on(s, u, 1.12, 0.01, 0.46, 0.66, 0.04, DARK);
    on(s, u, 1.45, 0.01, 0.46, 0.2, 0.04, DARK, prism);
    on(s, u, 1.45, 0.05, 0.6, 0.28, 0.06, PALE, prism);
    on(s, u, 0.78, 0.07, 0.58, 0.06, 0.16, STONE[1] as string);
    on(s, u, 0.9, 0.03, 0.06, 0.16, 0.06, '#d4d8de');
    if (opts.past) light(at(s, u, 1.04, 0.08), opts.flame, 0.7, 0.6, 1);
  };
  segs.forEach((s, si) => {
    const len = s.a.distanceTo(s.b);
    if (len < 0.05) return;
    const h = (s.h0 + s.h1) / 2;
    const mid = len / 2;
    const T = 0.9;
    // The wall's body (back from the face), its dark top merging with the rock mass behind.
    on(s, mid, h / 2, -T / 2, len + 0.02, h, T, STONE[si % 3] as string);
    on(s, mid, h + 0.02, -T / 2 - 0.1, len + 0.04, 0.04, T + 0.2, MASS);
    // The bench along its foot, the courses, the cornice.
    if (!s.end || s.h0 > 2) {
      on(s, mid, 0.17, 0.2, len, 0.34, 0.42, STONE_D[1] as string);
      on(s, mid, 0.35, 0.22, len, 0.04, 0.46, STONE_D[2] as string);
    } else on(s, mid, 0.14, 0.04, len, 0.28, 0.1, STONE_D[0] as string);
    on(s, mid, 0.95, 0.025, len, 0.06, 0.06, STONE_D[0] as string);
    if (h > 2) on(s, mid, h - 0.62, 0.03, len, 0.07, 0.07, STONE_D[0] as string);
    on(s, mid, h - 0.1, 0.07, len + 0.08, 0.2, 0.16, PALE);
    on(s, mid, h - 0.22, 0.04, len, 0.05, 0.1, STONE_D[2] as string);
    // Bays: a pilaster at each end and every ~1.8 between; in the bays a window or a niche, turn about.
    const n = Math.max(1, Math.round(len / 1.8));
    for (let i = 0; i <= n; i++) {
      const u = (i / n) * len;
      on(s, u, h / 2, 0.08, 0.28, h, 0.18, STONE[(i + si) % 3] as string);
      on(s, u, h - 0.36, 0.12, 0.38, 0.12, 0.26, PALE);
      on(s, u, 0.2, 0.12, 0.38, 0.4, 0.26, STONE_D[2] as string);
    }
    if (s.end && s.h0 < 2) return;
    for (let i = 0; i < n; i++) {
      const u = ((i + 0.5) / n) * len;
      if (s.end) continue;
      if ((i + si) % 2 === 0) lancet(s, u, h);
      else niche(s, u, h);
    }
  });
  // The rock mass behind the walls: dark and flat at the cornice's height (no cell's block shows).
  // (Drawn by the layer on the rock cells; here only the north wall's retable.)
  const north = segs.find((s) => s.end && s.h0 > 3) as Seg;
  const ax = hexToWorld(L.altar.q, L.altar.r).x * K;
  const u0 = ax - north.a.x;
  // The retable: a carved screen of three arches on a step, the middle one taller with the Hermitage's sun.
  on(north, u0, 0.15, 0.12, 2.9, 0.3, 0.3, STONE_D[1] as string);
  on(north, u0, 1.5, 0.06, 2.7, 2.6, 0.12, STONE[2] as string);
  on(north, u0, 2.86, 0.12, 2.9, 0.16, 0.22, PALE);
  on(north, u0, 2.95, 0.08, 2.7, 0.62, 0.1, STONE[2] as string, prism);
  for (const [k, w, top] of [
    [-1, 0.62, 2.0],
    [0, 0.84, 2.45],
    [1, 0.62, 2.0],
  ] as const) {
    const u = u0 + k * 0.92;
    const hh = top - 0.95;
    on(north, u, 0.95 + hh / 2, 0.13, w, hh, 0.02, '#4a5059');
    on(north, u, top, 0.13, w, 0.3, 0.02, '#4a5059', prism);
    for (const e of [-1, 1]) on(north, u + e * (w / 2 + 0.05), 0.95 + hh / 2, 0.16, 0.1, hh, 0.08, PALE);
    on(north, u, top + 0.02, 0.15, w + 0.2, 0.38, 0.06, PALE, prism);
  }
  // The sun in the middle arch: a pale disc, its rays.
  const sunAt = at(north, u0, 1.9, 0.16);
  kit.add(disc, '#c9ced6', place(sunAt.x, sunAt.y, sunAt.z, 0, new THREE.Vector3(0.36, 0.03, 0.36), Math.PI / 2));
  for (let i = 0; i < 12; i++) {
    const a = (i * Math.PI) / 6;
    const p = sunAt.clone().add(new THREE.Vector3(Math.cos(a) * 0.3, Math.sin(a) * 0.3, 0.01));
    kit.add(box, '#b4b9c1', place(p.x, p.y, p.z, 0, new THREE.Vector3(0.16, 0.035, 0.02), 0, a));
  }
  light(sunAt.clone().setZ(sunAt.z + 0.1), opts.past ? opts.flame : opts.cold, 1.6, 0.25, 0);
  box.dispose();
  prism.dispose();
  disc.dispose();
  return {
    face(x: number, y: number) {
      const p = new THREE.Vector2(x * K, y * K);
      let best: { d: number; q: THREE.Vector2; s: Seg } | null = null;
      for (const s of segs) {
        const d = s.b.clone().sub(s.a);
        const t = Math.max(0, Math.min(1, p.clone().sub(s.a).dot(d) / d.lengthSq()));
        const q = s.a.clone().add(d.multiplyScalar(t));
        const dd = q.distanceTo(p);
        if (!best || dd < best.d) best = { d: dd, q, s };
      }
      if (!best) return null;
      return { p: new THREE.Vector3(best.q.x, y0, best.q.y), n: new THREE.Vector3(best.s.n.x, 0, best.s.n.y) };
    },
  };
};
